/**
 * Non-component helpers for the Geography admin tab sections (WPS-222): zone
 * labelling, option lists derived from `/geo/meta/` and the zone list, and the
 * count line. Kept apart from the JSX in `geography-shared.tsx` so React fast
 * refresh sees a components-only module there.
 */

import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import type { SelectOption } from '@/shared/lib/forms'
import { useGeoZonesAll, type GeoZone } from '@/admin/hooks/use-geo'
import { useGeoMeta } from '@/admin/hooks/use-geo-reference'

export const DEFAULT_PAGE_SIZE = 20

/** `Name (CODE)` or just `Name` - how zones read everywhere on the tab. */
export function zoneLabel(name: string, code?: string | null): string {
  return code ? `${name} (${code})` : name
}

/** Zone options for a SELECT, optionally narrowed to one zone type. */
export function useZoneOptions(zoneType?: string): SelectOption[] {
  const { data } = useGeoZonesAll(zoneType ? { zone_type: zoneType } : undefined)
  return useMemo(
    () =>
      (data?.results ?? []).map((z: GeoZone) => ({
        value: z.uuid,
        label: zoneLabel(z.name, z.code),
      })),
    [data?.results],
  )
}

/** Zone-type label lookup from `/geo/meta/` (the Django `ZoneType` choices). */
export function useZoneTypeLabel(): (value: string) => string {
  const { data } = useGeoMeta()
  return useMemo(() => {
    const map = new Map((data?.zone_types ?? []).map((o) => [o.value, o.label]))
    return (value: string) => map.get(value) ?? value
  }, [data?.zone_types])
}

/**
 * Keep a row's stored value selectable when it predates the option list, so
 * opening the edit dialog never silently drops it.
 */
export function withCurrentValue(
  options: SelectOption[] | undefined,
  current: string | null | undefined,
): SelectOption[] {
  const opts = options ?? []
  if (current && !opts.some((o) => o.value === current)) {
    return [...opts, { value: current, label: current }]
  }
  return opts
}

/** Turn a paginated list's `{count}` into the "N things" count line. */
export function useCountLine(count: number | undefined, singular: string, plural: string) {
  const { t } = useTranslation()
  if (count === undefined) return t('geography.common.loading', 'Loading...')
  return count === 1 ? `1 ${singular}` : `${count.toLocaleString()} ${plural}`
}

